import { compact, find } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { isHotkeyPressed } from "react-hotkeys-hook";

import Emojify from "app/components/shared/Emojify";
import { CommandItem } from "app/components/shared/Command";
import { Step, Type as StepType } from "app/lib/pipeline";
import { useBuild } from "app/components/build/Show/lib/BuildContext";

import { getRelevantFilterForStep } from "../useFilterStore";

export function StepSearchItem({
  step,
  onSelect,
}: {
  step: Step;
  onSelect: (currentValue: string) => void;
}) {
  const { build } = useBuild();

  const [shiftPressed, setShiftPressed] = useState(false);

  useEffect(() => {
    function callback() {
      setShiftPressed(isHotkeyPressed("shift"));
    }

    document.addEventListener("keydown", callback);
    document.addEventListener("keyup", callback);

    return () => {
      document.removeEventListener("keydown", callback);
      document.removeEventListener("keyup", callback);
    };
  }, []);

  const label = useMemo(() => {
    switch (step.type) {
      case "command":
        return step.label || step.commandScript || "";
      case "input":
      case "block":
        return step.label || "Continue";
      default:
        return step.label || "";
    }
  }, [step]);

  const keywords = useMemo(() => {
    const state = getRelevantFilterForStep(step, build);

    const keywords: (string | null | undefined)[] = [label, state];

    if (step.type === StepType.Command) {
      keywords.push(step.commandScript);
    }

    if (step.groupUuid) {
      const group = find(build.steps, { uuid: step.groupUuid });

      keywords.push(group?.label);
    }

    return compact(keywords);
  }, [build, step, label]);

  return (
    <CommandItem
      className="group"
      key={step.uuid}
      value={step.uuid}
      keywords={keywords}
      onSelect={onSelect}
    >
      <Emojify text={label} className="truncate" />

      <span className="ml-auto whitespace-nowrap text-xs group-data-[selected=false]:hidden">
        {shiftPressed ? "Focus step" : "Go to step"}
      </span>
    </CommandItem>
  );
}
